import { useMemo } from 'react';
import { Person, Reservation, ServiceBooking } from '@turizi/shared';
import { Badge, Checkbox } from '@mantine/core';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDayShort, formatPrice } from '@/lib/format-utils';
import { RESERVATIONS_PATH } from '@/lib/navigation';
import { RESERVATION_STATUS, DEFAULT_CURRENCY } from '@turizi/shared';
import { ReservationClient } from '../reservation-ui';
import { MoneyNumberInput, ShowPaxGroup } from '../snippets';
import { IconUser, IconPhone, IconId } from '@tabler/icons-react';

interface Props {
    reservation: Reservation;
    isSelected: boolean;
    onSelectChange: (selected: boolean) => void;
    appliedAmount?: number;
    onAmountChange?: (amount: number) => void;
}

export default function ReservationUnpaidRow({
    reservation,
    isSelected,
    onSelectChange,
    appliedAmount,
    onAmountChange,
}: Props) {
    const { t } = useTranslation();

    const resStatus = reservation?.status || 'confirmed';
    const resStatusColor = RESERVATION_STATUS[resStatus]?.color || '#3b82f6';
    const resStart = reservation?.start_day;
    const resEnd = reservation?.end_day;

    const moneyInTotals = reservation?.price_per_currency || {};
    const currencies = Object.keys(moneyInTotals);
    const primaryCurrency = currencies.length > 0 ? currencies[0] : DEFAULT_CURRENCY;
    const totalAmount = Object.values(moneyInTotals).reduce((sum, amt) => sum + amt, 0);

    const bookings = reservation.service_bookings || [];

    // Extract persons by looping through bookings and reservation pax_groups
    const allPersons = useMemo(() => {
        const map = new Map<number, Person & { adults?: number; children?: number; seniors?: number; infants?: number; guests?: number }>();
        bookings.forEach(booking => {
            (booking.pax_groups || []).forEach(bg => {
                const pg = bg.pax_group;
                if (pg?.persons) {
                    pg.persons.forEach(person => {
                        if (person?.id && !map.has(person.id)) {
                            map.set(person.id, {
                                ...person,
                                adults: bg.adults ?? pg.adults ?? 0,
                                children: bg.children ?? pg.children ?? 0,
                                seniors: bg.seniors ?? pg.seniors ?? 0,
                                infants: bg.infants ?? pg.infants ?? 0,
                                guests: bg.guests ?? pg.guests ?? 0,
                            });
                        }
                    });
                }
            });
        });
        (reservation.pax_groups || []).forEach(group => {
            (group.persons || []).forEach(person => {
                if (person?.id && !map.has(person.id)) {
                    map.set(person.id, {
                        ...person,
                        adults: group.adults || 0,
                        children: group.children || 0,
                        seniors: group.seniors || 0,
                        infants: group.infants || 0,
                        guests: group.guests || 0,
                    });
                }
            });
        });
        return Array.from(map.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }, [bookings, reservation.pax_groups]);

    // const allPersons = useMemo(() => {
    //     const map = new Map<number, Person & { adults?: number; children?: number; seniors?: number; infants?: number; guests?: number }>();
    //     console.log({ reservation });

    //     reservation?.persons.map(person => {
    //         map.set(person.id, {
    //             ...person,
    //             adults: 3,
    //             children: 2,
    //             seniors: 1,
    //             infants: 0,
    //             guests: 0,
    //         });
    //     })
    //     return Array.from(map.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    // }, [bookings]);

    return (
        <div
            className={`p-3 rounded-lg border text-xs flex flex-col gap-2 transition-all w-full ${isSelected
                ? 'bg-slate-50 border-slate-400 shadow-sm'
                : 'bg-white border-slate-200 hover:border-slate-300'
                }`}
        >
            {/* Header row: Checkbox, Reservation ID badge & dates, Client business, Price */}
            <div className="flex flex-wrap items-center justify-between gap-2 w-full">
                <div className="flex flex-wrap items-center gap-2">
                    <Checkbox
                        size="xs"
                        color="black"
                        checked={isSelected}
                        onChange={(e) => onSelectChange(e.currentTarget.checked)}
                    />

                    {/* Reservation ID Badge & Link */}
                    <div className="flex items-center gap-1.5 bg-slate-100 px-2 py-0.5 rounded-md">
                        <Badge
                            circle
                            color={resStatusColor}
                            className="!w-2 !h-2 !p-0 shrink-0"
                        />
                        <Link
                            to={`${RESERVATIONS_PATH}/${reservation.id}`}
                            target="_blank"
                            className="font-bold text-sky-600 hover:underline text-[11px]"
                        >
                            #{reservation.id}
                        </Link>
                        {resStart && (
                            <span className="text-[10px] text-slate-500 font-medium">
                                {formatDayShort(resStart)} {resEnd && resEnd !== resStart ? `- ${formatDayShort(resEnd)}` : ''}
                            </span>
                        )}
                    </div>

                    {/* Client Business */}
                    <div className="text-[11px] font-medium text-slate-700 flex items-center gap-1">
                        <ReservationClient reservation={reservation} icon={false} />
                    </div>
                </div>

                {/* Total Price Amount */}
                <div className="font-bold text-emerald-600 text-xs flex items-baseline gap-1 shrink-0">
                    <span>{formatPrice(totalAmount)}</span>
                    <span className="text-[10px] font-bold text-slate-800">{primaryCurrency}</span>
                </div>
            </div>

            {/* Sub-row: Summary of Paxs & Bookings info */}
            <div className="flex flex-col gap-1.5 pl-6 bg-slate-50/50 p-2 rounded-md border border-slate-100 w-full">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex flex-wrap items-center gap-1.5">
                            {allPersons.map(p => (
                                <div key={p.id} className="flex flex-wrap items-center gap-2 bg-white px-2 py-0.5 rounded border border-slate-200/80 shadow-2xs text-xs">
                                    <div className="flex items-center gap-1 font-bold text-slate-800">
                                        <IconUser size={13} className="shrink-0 text-blue-600" stroke={1.5} />
                                        <span>{p.name}</span>
                                        <ShowPaxGroup
                                            adults={p.adults}
                                            children={p.children}
                                            seniors={p.seniors}
                                            infants={p.infants}
                                            guests={p.guests}
                                        />
                                    </div>
                                    {p.phone && (
                                        <div className="flex items-center gap-0.5 text-gray-800 font-normal">
                                            <IconPhone size={11} className="shrink-0 text-slate-400" />
                                            <span>{p.phone}</span>
                                        </div>
                                    )}
                                    {p.document_number && (
                                        <div className="flex items-center gap-0.5 text-gray-800 font-normal">
                                            <IconId size={11} className="shrink-0 text-slate-400" />
                                            <span>{p.document_number}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        {/* not useful, actually confusiong */}
                        {/* {bookings.length > 0 && (
                            <span className="text-[10px] text-slate-500 font-medium shrink-0">
                                ({bookings.length} {t('services')})
                            </span>
                        )} */}
                    </div>

                    {reservation.observations && (
                        <div
                            className="text-[10px] text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded font-medium max-w-[200px] truncate"
                            title={reservation.observations}
                        >
                            {reservation.observations}
                        </div>
                    )}
                </div>
            </div>

            {/* If selected: Amount input */}
            {isSelected && onAmountChange && (() => {
                const currentAmount = appliedAmount !== undefined ? appliedAmount : totalAmount;
                return (
                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200 mt-1 pl-6">
                        <span className="text-[11px] text-slate-500 font-medium">{t('amount')}:</span>
                        <MoneyNumberInput
                            size="sm"
                            className="w-36"
                            amount={currentAmount}
                            onChange={(val) => onAmountChange(Number(val || 0))}
                            error={currentAmount <= 0 ? t('amount_must_be_greater_than_zero') : undefined}
                        />
                    </div>
                );
            })()}
        </div>
    );
}
